import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { doc, getDoc, updateDoc, serverTimestamp, collection, query, where, getDocs, addDoc, onSnapshot } from "firebase/firestore";
import Spinner from './Spinner.js';
import { getNextMocNumber } from './firebaseUtils.js';
import { ArrowLeft, Edit2, CheckCircle, Copy, Printer, Send, Package, Clock, AlertCircle } from 'lucide-react';
import HorizontalTimeline from './components/HorizontalTimeline.js';

const MOCPreview = ({ db }) => {
  const { mocId } = useParams();
  const navigate = useNavigate();
  const [moc, setMoc] = useState(null);
  const [order, setOrder] = useState(null);
  const [dispatches, setDispatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isUpdating, setIsUpdating] = useState(false);
  const printRef = useRef();

  useEffect(() => {
    const fetchMoc = async () => {
      setLoading(true);
      try {
        const docRef = doc(db, "dispatches", mocId);
        const docSnap = await getDoc(docRef);
        if (docSnap.exists()) {
          const mocData = { id: docSnap.id, ...docSnap.data() };
          setMoc(mocData);

          if (mocData.orderId) {
            const orderSnap = await getDoc(doc(db, "orders", mocData.orderId));
            if (orderSnap.exists()) {
              setOrder({ id: orderSnap.id, ...orderSnap.data() });
            }
          }
        } else {
          alert("MOC not found");
          navigate('/ops/shipments');
        }
      } catch (error) {
        console.error("Error fetching MOC:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchMoc();
  }, [db, mocId, navigate]);

  // Real-time subscription to dispatches for the timeline
  useEffect(() => {
    if (!moc?.orderId) return;
    const q = query(collection(db, "dispatches"), where("orderId", "==", moc.orderId));
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const dispatchesList = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setDispatches(dispatchesList);
    });
    return () => unsubscribe();
  }, [db, moc?.orderId]);

  const calculateFulfillment = async (orderId, currentMocId = null) => {
      const q = query(collection(db, "dispatches"), where("orderId", "==", orderId));
      const snap = await getDocs(q);
      const map = {};
      snap.docs.forEach(docSnap => {
          if (docSnap.id === currentMocId) return; 
          const data = docSnap.data();
          if (data.dispatchStatus === 'packing_rejected') return; 
          
          data.components?.forEach(c => {
              if (!map[c.sku]) map[c.sku] = { new: 0, rental: 0 };
              map[c.sku].new += (parseInt(c.newQty) || 0);
              map[c.sku].rental += (parseInt(c.rentalQty) || 0);
          });
      });
      return map;
  };

  const handleStatusUpdate = async (newDispatchStatus, newOrderStatus, confirmMsg, successMsg) => {
    if (confirmMsg && !window.confirm(confirmMsg)) return;
    
    setIsUpdating(true);
    try {
      // 1. Update Current MOC
      await updateDoc(doc(db, "dispatches", mocId), {
        dispatchStatus: newDispatchStatus,
        updatedAt: serverTimestamp()
      });

      // 2. If submitting to finance, handle splitting logic
      if (newDispatchStatus === 'moc_pending_finance' && moc.orderId) {
          const fulfillmentMap = await calculateFulfillment(moc.orderId, mocId);
          
          const remainingComponents = moc.components.map(c => {
              // Note: We use fulfillmentMap which excludes current MOC
              const prevMocd = (moc.typeOfMOC === 'Rental') 
                  ? (fulfillmentMap[c.sku]?.rental || 0) 
                  : (fulfillmentMap[c.sku]?.new || 0);
              
              const currentMocd = (moc.typeOfMOC === 'Rental') ? (c.rentalQty || 0) : (c.newQty || 0);
              const totalMocdSoFar = prevMocd + currentMocd;
              const balance = Math.max(0, (c.orderQty || 0) - totalMocdSoFar);

              if (balance > 0) {
                  return {
                      ...c,
                      newQty: moc.typeOfMOC === 'Rental' ? 0 : balance,
                      rentalQty: moc.typeOfMOC === 'Rental' ? balance : 0,
                      ticked: true
                  };
              }
              return null;
          }).filter(c => c !== null);

          if (remainingComponents.length > 0) {
              const nextMocNo = await getNextMocNumber(db);
              const draftPayload = {
                  ...moc,
                  mocNo: nextMocNo,
                  mocDate: new Date().toISOString().split('T')[0],
                  components: remainingComponents,
                  dispatchStatus: 'moc_draft',
                  updatedAt: serverTimestamp(),
                  createdAt: serverTimestamp(),
              };
              delete draftPayload.id;
              await addDoc(collection(db, "dispatches"), draftPayload);
              console.log("Auto-created draft MOC for balance:", nextMocNo);
          }
      }

      // 3. Update Order Status
      if (moc.orderId) {
        await updateDoc(doc(db, "orders", moc.orderId), {
          status: newOrderStatus,
          updatedAt: serverTimestamp()
        });
      }

      setMoc(prev => ({ ...prev, dispatchStatus: newDispatchStatus }));
      alert(successMsg + (newDispatchStatus === 'moc_pending_finance' ? ". Draft for remaining balance created (if any)." : ""));
      
      if (newDispatchStatus === 'packing_queued') {
        navigate('/ops/packing');
      }
    } catch (error) {
      alert("Error: " + error.message);
    } finally {
      setIsUpdating(false);
    }
  };

  const handleClone = () => {
    const clonedData = { 
        ...moc, 
        mocNo: '', 
        mocDate: new Date().toISOString().split('T')[0],
        dispatchStatus: 'moc_draft'
    };
    delete clonedData.id;
    navigate('/ops/moc/new', { state: { orderId: moc.orderId, initialData: clonedData } });
  };

  const handlePrint = () => {
    const printContent = printRef.current.innerHTML;
    const printWindow = window.open('', '_blank');
    printWindow.document.write(`
      <html>
        <head>
          <title>MOC ${moc.mocNo}</title>
          <style>
            body { font-family: Arial, sans-serif; color: #000; }
            table { width: 100%; border-collapse: collapse; margin-bottom: 15px; }
            td, th { border: 1px solid #000 !important; padding: 8px; text-align: left; font-size: 13px; }
            th { font-weight: bold; background-color: #f2f2f2; }
            .total-meter-number { font-size: 30px; font-weight: 900; color: #000; }
            @page { margin: 1cm; }
          </style>
        </head>
        <body>${printContent}</body>
      </html>
    `);
    printWindow.document.close();
    printWindow.focus();
    setTimeout(() => { printWindow.print(); printWindow.close(); }, 250);
  };

  if (loading) return <Spinner />;

  // --- BUTTON LOGIC ---
  const renderPrimaryButton = () => {
    const status = moc.dispatchStatus;

    if (!status || status === 'moc_draft') {
      return (
        <button 
          onClick={() => handleStatusUpdate('moc_pending_finance', 'pending_finance', "Submit this MOC to Finance?", "MOC Submitted to Finance")} 
          disabled={isUpdating} 
          style={confirmBtnStyle}
        >
          <Send size={18} /> Submit to Finance
        </button>
      );
    }

    if (status === 'moc_approved' || status === 'confirmed') {
      return (
        <button 
          onClick={() => handleStatusUpdate('packing_queued', 'packing_queued', "Move this MOC to Packing Module?", "Moved to Packing Module")} 
          disabled={isUpdating} 
          style={confirmBtnStyle}
        >
          <Package size={18} /> Schedule Packing
        </button>
      );
    }

    if (status === 'packing_scheduled') {
      return (
        <button 
          onClick={() => handleStatusUpdate('packing', 'packing', "Confirm and start packing?", "Packing Confirmed")} 
          disabled={isUpdating} 
          style={confirmBtnStyle}
        >
          <CheckCircle size={18} /> Confirm Packing
        </button>
      );
    }

    // Default label for progress statuses
    const labels = {
      'moc_pending_finance': 'Waiting for Finance',
      'packing_queued': 'Awaiting Schedule',
      'packing': 'Packing in Progress',
      'packed_ready': 'Packed & Ready'
    };

    return (
      <button disabled style={{ ...confirmBtnStyle, backgroundColor: '#f1f5f9', color: '#64748b' }}>
        {labels[status] || status.replace('_', ' ').toUpperCase()}
      </button>
    );
  };

  return (
    <div style={{ maxWidth: '1000px', margin: '0 auto', padding: '20px' }}>
      {/* FIN HOLD BANNER */}
      {moc.dispatchStatus === 'fin_hold' && (
        <div style={{ backgroundColor: '#fffbeb', border: '1px solid #f59e0b', borderRadius: '8px', padding: '15px', marginBottom: '20px', display: 'flex', alignItems: 'flex-start', gap: '15px', animation: 'fadeIn 0.3s' }}>
          <Clock size={24} color="#f59e0b" style={{ flexShrink: 0, marginTop: '2px' }} />
          <div style={{ flex: 1 }}>
            <h3 style={{ margin: '0 0 5px 0', color: '#b45309', fontSize: '16px' }}>MOC On Hold (Finance)</h3>
            <p style={{ margin: 0, color: '#92400e', fontSize: '14px', lineHeight: '1.5' }}>
              <strong>Reason:</strong> {moc.finHoldReason || 'Awaiting finance clarification.'}
            </p>
            <p style={{ margin: '10px 0 0 0', color: '#78350f', fontSize: '13px' }}>
              This MOC is paused. You cannot proceed until finance resolves the hold.
            </p>
          </div>
        </div>
      )}

      {/* FIN REJECTED BANNER */}
      {moc.dispatchStatus === 'fin_rejected' && (
        <div style={{ backgroundColor: '#fef2f2', border: '1px solid #ef4444', borderRadius: '8px', padding: '15px', marginBottom: '20px', display: 'flex', alignItems: 'flex-start', gap: '15px', animation: 'fadeIn 0.3s' }}>
          <AlertCircle size={24} color="#ef4444" style={{ flexShrink: 0, marginTop: '2px' }} />
          <div style={{ flex: 1 }}>
            <h3 style={{ margin: '0 0 5px 0', color: '#991b1b', fontSize: '16px' }}>MOC Rejected by Finance</h3>
            <p style={{ margin: 0, color: '#b91c1c', fontSize: '14px', lineHeight: '1.5' }}>
              <strong>Reason:</strong> {moc.finRejectionReason || 'No specific reason provided.'}
            </p>
            <p style={{ margin: '10px 0 0 0', color: '#7f1d1d', fontSize: '13px' }}>
              This MOC has been rejected and returned to the Sales team. It can no longer be edited or submitted by Operations.
            </p>
          </div>
        </div>
      )}

      {/* Header Buttons */}
      <div className="no-print" style={headerStyle}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
          <button onClick={() => navigate('/ops/shipments')} style={backBtnStyle}>
            <ArrowLeft size={20} />
          </button>
          <h2 style={{ margin: 0 }}>MOC Preview: {moc.mocNo}</h2>
        </div>
        <div style={{ display: 'flex', gap: '10px' }}>
          {(!moc.dispatchStatus || moc.dispatchStatus === 'moc_draft') && moc.dispatchStatus !== 'fin_hold' && (
            <button onClick={() => navigate(`/ops/moc/edit/${moc.id}`)} style={editBtnStyle}>
              <Edit2 size={18} /> Edit
            </button>
          )}

          {moc.dispatchStatus !== 'fin_hold' && renderPrimaryButton()}

          <button onClick={handleClone} style={secondaryBtnStyle}>
            <Copy size={18} /> Clone
          </button>
          <button onClick={handlePrint} style={secondaryBtnStyle}>
            <Printer size={18} /> Export PDF
          </button>
        </div>
      </div>

      {/* PDF Preview Area */}
      <div ref={printRef} style={pdfContainerStyle} className="print-area">
        {/* ... (Existing MOC Header & Table logic remains same) ... */}
        <table style={headerTableStyle}>
          <tbody>
            <tr><td colSpan="5" style={{ ...headerTableCellStyle, textAlign: 'center', backgroundColor: '#f1f5f9', fontWeight: '800', fontSize: '18px' }}>PACKAGING/BILL OF MATERIAL</td></tr>
            <tr>
              <td style={labelCellStyle}>Document date</td><td style={valueCellStyle}>{moc.mocDate}</td>
              <td style={labelCellStyle}>Document No.</td><td style={valueCellStyle}>{moc.mocNo}</td>
              <td style={valueCellStyle}><strong>Order No:</strong> {moc.orderNumber || '---'}</td>
            </tr>
            <tr>
              <td style={labelCellStyle}>Type of MOC</td><td style={valueCellStyle}>{moc.typeOfMOC}</td>
              <td style={labelCellStyle}>Document type</td><td style={valueCellStyle}>{moc.documentType}</td>
              <td style={{...valueCellStyle, backgroundColor: '#fdf2f2'}}><strong>ALW:</strong> {moc.aluminiumWeight} kg</td>
            </tr>
            <tr>
              <td style={labelCellStyle}>PO Number</td><td style={valueCellStyle}>{moc.poNumber}</td>
              <td style={labelCellStyle}>PO DATE</td><td style={valueCellStyle}>{moc.poDate}</td>
              <td style={valueCellStyle}><strong>Warehouse:</strong> {moc.warehouse}</td>
            </tr>
            <tr>
              <td style={labelCellStyle}>Consignee</td><td colSpan="3" style={{...valueCellStyle, fontWeight: 'bold'}}>{moc.customerName}</td>
              <td rowSpan="4" style={{ ...headerTableCellStyle, textAlign: 'center', verticalAlign: 'middle', width: '120px' }}>
                <strong>Total Meter</strong><br /><span style={{ fontSize: '30px', fontWeight: '900' }}>{moc.totalMeterNumber}</span>
              </td>
            </tr>
            <tr><td style={labelCellStyle}>Shipping Address</td><td colSpan="3" style={{ ...valueCellStyle, height: '60px', verticalAlign: 'top' }}>{moc.shippingAddress}</td></tr>
            <tr><td style={labelCellStyle}>Material Code</td><td colSpan="3" style={valueCellStyle}>{moc.materialCode}</td></tr>
            <tr><td style={labelCellStyle}>Product Summary</td><td colSpan="3" style={valueCellStyle}>{moc.productSummary}</td></tr>
          </tbody>
        </table>

        {/* Component Table */}
        <table style={pdfTableStyle}>
          <thead>
            <tr style={{ backgroundColor: '#f8fafc' }}>
              <th style={pdfThStyle}>S.No</th><th style={pdfThStyle}>SKU</th><th style={{ ...pdfThStyle, textAlign: 'left' }}>Item Name</th><th style={pdfThStyle}>Order Qty</th><th style={pdfThStyle}>New Qty</th><th style={pdfThStyle}>Rental Qty</th><th style={pdfThStyle}>Total Weight</th>
            </tr>
          </thead>
          <tbody>
            {moc.components?.map((c, i) => (
              <tr key={i}>
                <td style={pdfTdStyle}>{i + 1}</td><td style={pdfTdStyle}>{c.sku}</td><td style={{ ...pdfTdStyle, textAlign: 'left' }}>{c.name}</td><td style={pdfTdStyle}>{c.orderQty || 0}</td><td style={pdfTdStyle}>{c.newQty || 0}</td><td style={pdfTdStyle}>{c.rentalQty || 0}</td><td style={pdfTdStyle}>{((parseInt(c.newQty || 0) + parseInt(c.rentalQty || 0)) * parseFloat(c.weight || 0)).toFixed(2)} kg</td>
              </tr>
            ))}
            <tr style={{ fontWeight: 'bold', backgroundColor: '#f2f2f2' }}>
              <td colSpan="6" style={{ ...pdfTdStyle, textAlign: 'right' }}>TOTAL WEIGHT</td><td style={pdfTdStyle}>{parseFloat(moc.totalWeight || 0).toFixed(2)} kg</td>
            </tr>
          </tbody>
        </table>

        {/* Material Checklist */}
        <table style={headerTableStyle}>
          <tbody>
            <tr><td style={labelCellStyle}>Stickers</td><td style={valueCellStyle}>{moc.materialChecklist?.stickers ? 'YES' : 'NO'}</td><td style={labelCellStyle}>Laser Marking</td><td style={valueCellStyle}>{moc.materialChecklist?.laserMarking ? 'YES' : 'NO'}</td><td style={labelCellStyle}>Material Wrapping</td><td style={valueCellStyle}>{moc.materialChecklist?.materialWrapping ? 'YES' : 'NO'}</td></tr>
            <tr><td style={labelCellStyle}>Instructions</td><td colSpan="5" style={{ ...valueCellStyle, minHeight: '50px' }}>{moc.materialChecklist?.materialInstructions || 'None'}</td></tr>
          </tbody>
        </table>

        {/* Verification Section */}
        <table style={headerTableStyle}>
          <tbody>
            <tr>
              <td style={labelCellStyle}>Prepared by</td><td style={valueCellStyle}>{moc.preparedBy}</td>
              <td rowSpan="6" style={{ ...headerTableCellStyle, verticalAlign: 'top', fontSize: '11px', width: '45%', borderLeft: '2px solid #000' }}>
                <div style={{fontWeight: 'bold', marginBottom: '5px', textDecoration: 'underline'}}>NOTICE FOR SALES TEAM:</div>
                Sales Team, please verify the Material Quantity Against your BOM Before Signing the MOC. Any Discrepancies in the MOC Will Not Be the Responsibility of the Operations Team, As It Has Been Prepared Exactly As Per Your BOM.
              </td>
            </tr>
            <tr><td style={labelCellStyle}>Qty Verified By (Approval)</td><td style={valueCellStyle}>{moc.quantityVerifiedBy}</td></tr>
            <tr><td style={labelCellStyle}>Qty Verified By (Dispatch)</td><td style={valueCellStyle}>{moc.quantityVerifiedByDispatch}</td></tr>
            <tr><td style={labelCellStyle}>Vehicle Number</td><td style={valueCellStyle}>{moc.vehicleNumber}</td></tr>
            <tr><td style={labelCellStyle}>Vehicle In Time</td><td style={valueCellStyle}>{moc.vehicleInTime}</td></tr>
            <tr><td style={labelCellStyle}>Vehicle Out Time</td><td style={valueCellStyle}>{moc.vehicleOutTime}</td></tr>
            <tr><td style={labelCellStyle}>Date</td><td style={valueCellStyle}>{moc.vehicleDate}</td><td style={{ ...headerTableCellStyle, textAlign: 'center', fontWeight: 'bold', fontSize: '14px', backgroundColor: '#f2f2f2' }}>SIGNATURE</td></tr>
          </tbody>
        </table>

        {/* Finance Section */}
        <table style={headerTableStyle}>
          <tbody>
            <tr><td colSpan="4" style={{ ...headerTableCellStyle, textAlign: 'center', backgroundColor: '#f1f5f9', fontWeight: '800' }}>FOR FINANCE USE ONLY</td></tr>
            <tr><td style={{ ...labelCellStyle, width: '25%' }}>Payment Terms</td><td style={{ ...valueCellStyle, width: '25%' }}>{moc.paymentTerms}</td><td style={{ ...labelCellStyle, width: '25%' }}>Payment Status</td><td style={{ ...valueCellStyle, width: '25%' }}>{moc.paymentStatus}</td></tr>
            <tr><td style={labelCellStyle}>Last Payment amount</td><td style={valueCellStyle}>{moc.lastPaymentAmount}</td><td style={labelCellStyle}>Outstanding Amount</td><td style={valueCellStyle}>{moc.outstandingAmount}</td></tr>
            <tr><td style={labelCellStyle}>Payment Rec. Date</td><td style={valueCellStyle}>{moc.paymentRecDate}</td><td style={labelCellStyle}>Outstanding days</td><td style={valueCellStyle}>{moc.outstandingDays}</td></tr>
            <tr><td style={labelCellStyle}>Sign/Name (Finance)</td><td style={valueCellStyle}>{moc.approvedByFinance}</td><td style={labelCellStyle}>Salesperson sign/name</td><td style={valueCellStyle}>{moc.approvedBySalesperson}</td></tr>
          </tbody>
        </table>
        
        <div style={{ marginTop: '100px', display: 'flex', justifyContent: 'space-between', padding: '0 20px', alignItems: 'flex-end' }}>
          <div style={{ ...signLineStyle, position: 'relative' }}>
            {moc.approvedBySalesperson && (
              ['Rohit Soni', 'Rajesh Soni', 'Sanjay Kumawat'].includes(moc.approvedBySalesperson) ? (
                <>
                  {moc.approvedBySalesperson === 'Rohit Soni' && <img src="/Rohit-Soni-Sign.png" alt="Sign" style={signImageOverlayStyle} />}
                  {moc.approvedBySalesperson === 'Rajesh Soni' && <img src="/Rajesh-Soni-Sign.png" alt="Sign" style={signImageOverlayStyle} />}
                  {moc.approvedBySalesperson === 'Sanjay Kumawat' && <img src="/Sanjay-Kumawat-Sign.png" alt="Sign" style={signImageOverlayStyle} />}
                </>
              ) : (
                <img src="/placeholder-sign.png" alt="Sign" style={{ ...signImageOverlayStyle, maxHeight: '50px', bottom: '35px' }} />
              )
            )}
            Salesperson
          </div>
          <div style={{ ...signLineStyle, position: 'relative' }}>
            {moc.approvedByFinance === 'Jasveer Singh' && (
              <img src="/Jasveer-sir-Sign.png" alt="Sign" style={signImageOverlayStyle} />
            )}
            {moc.approvedByFinance === 'Tikkam' && (
              <img src="/Tikkam-Sign.png" alt="Sign" style={signImageOverlayStyle} />
            )}
            {moc.approvedByFinance === 'Saurabh' && (
              <img src="/Saurabh-Ganeriwal-Sign.png" alt="Sign" style={signImageOverlayStyle} />
            )}
            For Finance Approval
          </div>
          <div style={{ ...signLineStyle, position: 'relative' }}>
            {moc.quantityVerifiedBy === 'Ghanshyam Prajapat' && (
              <img src="/Ghansyam-Sign.png" alt="Sign" style={signImageOverlayStyle} />
            )}
            {moc.quantityVerifiedBy === 'Rajesh Kumar' && (
              <img src="/Rajesh-Kumar-Sign.png" alt="Sign" style={signImageOverlayStyle} />
            )}
            Operations Head
          </div>
        </div>
      </div>

      {/* FOOTER TIMELINE */}
      {!loading && moc && (
        <div style={{ 
          marginTop: '40px', 
          maxWidth: '1000px', 
          margin: '50px auto 20px auto',
          animation: 'fadeIn 0.5s ease-in-out'
        }}>
          <h3 style={{ 
            fontSize: '16px', 
            fontWeight: 'bold', 
            color: '#374151', 
            marginBottom: '15px',
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
          }}>
            <Clock size={18} color="#2563eb" /> Order Timeline
          </h3>
          <HorizontalTimeline orderData={order} dispatchesData={dispatches} />
        </div>
      )}

      <style dangerouslySetInnerHTML={{ __html: `@media print { .no-print { display: none !important; } }`}} />
    </div>
  );
};

// --- STYLES ---
const headerStyle = { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', padding: '10px 0', borderBottom: '1px solid #e2e8f0' };
const backBtnStyle = { background: 'none', border: 'none', cursor: 'pointer', color: '#64748b' };
const editBtnStyle = { backgroundColor: '#2563eb', color: 'white', border: 'none', padding: '8px 16px', borderRadius: '6px', fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' };
const confirmBtnStyle = { backgroundColor: '#059669', color: 'white', border: 'none', padding: '8px 16px', borderRadius: '6px', fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' };
const secondaryBtnStyle = { backgroundColor: 'white', color: '#374151', border: '1px solid #d1d5db', padding: '8px 16px', borderRadius: '6px', fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' };
const pdfContainerStyle = { backgroundColor: 'white', padding: '40px', border: '1px solid #e2e8f0', boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)', minHeight: '1100px', width: '100%', boxSizing: 'border-box', color: '#000' };
const headerTableStyle = { width: '100%', borderCollapse: 'collapse', marginBottom: '15px', border: '1px solid #000' };
const headerTableCellStyle = { border: '1px solid #000', padding: '8px', fontSize: '13px' };
const labelCellStyle = { ...headerTableCellStyle, fontWeight: 'bold', backgroundColor: '#f2f2f2' };
const valueCellStyle = { ...headerTableCellStyle, backgroundColor: '#fff' };
const pdfTableStyle = { width: '100%', borderCollapse: 'collapse', marginTop: '15px', border: '1px solid #000' };
const pdfThStyle = { border: '1px solid #000', padding: '6px', fontSize: '12px', textAlign: 'center', fontWeight: 'bold' };
const pdfTdStyle = { border: '1px solid #000', padding: '6px', fontSize: '12px', textAlign: 'center' };
const signLineStyle = { borderTop: '1px solid #000', width: '180px', textAlign: 'center', paddingTop: '10px', fontSize: '12px', fontWeight: 'bold' };
const signImageOverlayStyle = { 
  position: 'absolute', 
  bottom: '30px', 
  left: '50%', 
  transform: 'translateX(-50%)', 
  maxHeight: '100px',
  pointerEvents: 'none'
};

export default MOCPreview;
